import React from 'react';
import {
  DollarSign,
  Globe,
  Briefcase,
  Award,
  Cpu,
  BarChart3,
  Gamepad2,
  Bitcoin,
} from 'lucide-react';
import { Typography } from '../styles/primitives';
import { FormattedMarket } from '../services/polymarketApi';

const categoryIcons: Record<string, React.ComponentType<{ size?: number; style?: React.CSSProperties }>> = {
  politics: Briefcase,
  sports: Award,
  crypto: Bitcoin,
  economy: BarChart3,
  tech: Cpu,
  general: Gamepad2,
};

export function formatVolume(vol: number): string {
  if (vol >= 1_000_000) return `$${(vol / 1_000_000).toFixed(vol >= 10_000_000 ? 0 : 1)}M`;
  if (vol >= 1_000) return `$${(vol / 1_000).toFixed(vol >= 10_000 ? 0 : 1)}K`;
  if (vol > 0) return `$${vol.toFixed(0)}`;
  return '$0';
}

interface EventMarketCardProps {
  market: FormattedMarket;
  onClick: () => void;
}

const EventMarketCard: React.FC<EventMarketCardProps> = ({ market, onClick }) => {
  const CategoryIcon = categoryIcons[market.category] || Globe;
  const topOutcomes = market.outcomes.slice(0, 3);

  const isBinary =
    market.outcomes.length === 2 &&
    market.outcomes.some(o => o.name.toLowerCase() === 'yes') &&
    market.outcomes.some(o => o.name.toLowerCase() === 'no');

  const yesOutcome = market.outcomes.find(o => o.name.toLowerCase() === 'yes');
  const noOutcome = market.outcomes.find(o => o.name.toLowerCase() === 'no');

  return (
    <div
      onClick={onClick}
      className="cursor-pointer group transition-all duration-200 hover:-translate-y-0.5 flex flex-col h-full"
      style={{
        background: '#ffffff',
        border: '1px solid rgba(0,0,0,0.07)',
        borderRadius: '16px',
        overflow: 'hidden',
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
      }}
    >
      <div className="p-4 pb-3 flex items-start gap-3">
        {market.image ? (
          <img
            src={market.image}
            alt=""
            className="flex-shrink-0"
            style={{ width: 40, height: 40, borderRadius: '10px', objectFit: 'cover' }}
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        ) : (
          <div
            className="flex-shrink-0 flex items-center justify-center"
            style={{
              width: 40,
              height: 40,
              borderRadius: '10px',
              background: 'rgba(217, 18, 34, 0.08)',
            }}
          >
            <CategoryIcon size={20} style={{ color: '#d91222' }} />
          </div>
        )}
        <h3
          style={{
            fontSize: Typography.BodyMedium,
            fontWeight: Typography.WeightSemibold,
            color: '#1e293b',
            lineHeight: Typography.LeadingTight,
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical' as const,
            overflow: 'hidden',
          }}
        >
          {market.question}
        </h3>
      </div>

      <div className="px-4 pb-3 flex-1">
        {isBinary ? (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#10b981' }} />
                <span style={{ fontSize: Typography.BodySmall, color: '#475569' }}>Yes</span>
              </div>
              <span
                className="font-mono"
                style={{
                  fontSize: Typography.HeadlineSmall,
                  fontWeight: Typography.WeightBold,
                  color: '#1e293b',
                }}
              >
                {yesOutcome?.probability.toFixed(0)}%
              </span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#ef4444' }} />
                <span style={{ fontSize: Typography.BodySmall, color: '#475569' }}>No</span>
              </div>
              <span
                className="font-mono"
                style={{
                  fontSize: Typography.HeadlineSmall,
                  fontWeight: Typography.WeightBold,
                  color: '#1e293b',
                }}
              >
                {noOutcome?.probability.toFixed(0)}%
              </span>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            {topOutcomes.map((outcome) => (
              <div key={outcome.name} className="flex items-center justify-between gap-2">
                <span
                  style={{
                    fontSize: Typography.BodySmall,
                    color: '#475569',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    flex: 1,
                  }}
                >
                  {outcome.name}
                </span>
                <span
                  className="font-mono flex-shrink-0"
                  style={{
                    fontSize: Typography.BodySmall,
                    fontWeight: Typography.WeightBold,
                    color: '#1e293b',
                  }}
                >
                  {outcome.probability.toFixed(0)}%
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="px-4 pb-3">
        {isBinary ? (
          <div className="flex gap-2">
            <div
              className="flex-1 py-2 text-center"
              style={{
                background: 'rgba(16, 185, 129, 0.1)',
                color: '#10b981',
                fontSize: Typography.BodySmall,
                fontWeight: Typography.WeightSemibold,
                borderRadius: '8px',
                border: '1px solid rgba(16, 185, 129, 0.2)',
              }}
            >
              Yes
            </div>
            <div
              className="flex-1 py-2 text-center"
              style={{
                background: 'rgba(239, 68, 68, 0.08)',
                color: '#ef4444',
                fontSize: Typography.BodySmall,
                fontWeight: Typography.WeightSemibold,
                borderRadius: '8px',
                border: '1px solid rgba(239, 68, 68, 0.2)',
              }}
            >
              No
            </div>
          </div>
        ) : (
          <div className="flex gap-2">
            {market.outcomes.slice(0, 2).map((outcome) => (
              <div
                key={outcome.name}
                className="flex-1 py-2 text-center"
                style={{
                  background: 'rgba(217, 18, 34, 0.06)',
                  color: '#d91222',
                  fontSize: Typography.Caption,
                  fontWeight: Typography.WeightSemibold,
                  borderRadius: '8px',
                  border: '1px solid rgba(217, 18, 34, 0.12)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  padding: '6px 8px',
                }}
              >
                {outcome.name.length > 14 ? outcome.name.substring(0, 14) + '...' : outcome.name}
              </div>
            ))}
          </div>
        )}
      </div>

      <div
        className="px-4 py-3 flex items-center justify-between mt-auto"
        style={{ borderTop: '1px solid rgba(0,0,0,0.05)' }}
      >
        <div className="flex items-center gap-1.5">
          <DollarSign size={12} style={{ color: '#94a3b8' }} />
          <span className="font-mono" style={{ fontSize: Typography.Fine, color: '#94a3b8' }}>
            {formatVolume(market.volume)} Vol.
          </span>
        </div>
        <span
          style={{
            fontSize: Typography.Fine,
            color: '#94a3b8',
            textTransform: 'capitalize',
          }}
        >
          {market.category}
        </span>
      </div>
    </div>
  );
};

export default EventMarketCard;
